import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toErrorMessage } from '../api/client';
import {
  approveKyb,
  rejectKyb,
  submitKyb,
  updateClientBeta,
  updateCompanyContact,
} from '../api/endpoints';
import type { AccessStage, ClientAccessView, KybSubmitRequest } from '../api/types';
import { formatDate, formatDateTime } from '../utils/format';
import { formatPhoneDisplay, formatUsPhoneInput, phoneForEdit, validatePhone, validateWebsite, digitsOnly } from '../utils/validation';
import { Modal } from './Modal';
import { ThresholdSlider } from './ThresholdSlider';
import { useToast } from './ToastProvider';

/**
 * The road to live access, shared by My Company (the client) and the client page (super admin):
 *
 *   Sandbox → live access request (reviewed within 24 h, else approved automatically)
 *           → Beta Live (free, N/day, D days) → paid plan (Live)
 *
 * The rules are all in Core (ClientAccess, IBusinessVerificationService). These components show
 * the state and collect input. The Razor portal has the same cards in Account/Company and
 * Clients/Detail.
 */

// ── Stage badge ───────────────────────────────────────────────────────────────

const STAGE_CLASS: Record<AccessStage, string> = {
  Sandbox: 'bg-info-subtle text-info border border-info-subtle',
  BetaLive: 'bg-primary-subtle text-primary border border-primary-subtle',
  BetaEnded: 'bg-warning-subtle text-warning border border-warning-subtle',
  Live: 'bg-success-subtle text-success border border-success-subtle',
};

/** `outline` is the card-header style: a neutral outline badge, like every other card header. */
export function StageBadge({ stage, label, outline = false }: { stage: AccessStage; label: string; outline?: boolean }) {
  return (
    <span className={`badge ${outline ? 'badge-outline theme-secondary' : `badge-status ${STAGE_CLASS[stage]}`}`}>
      <i className={`bi ${stage === 'Live' ? 'bi-lightning-charge-fill' : stage === 'BetaLive' ? 'bi-rocket-takeoff' : 'bi-flask'} me-1`} />
      {label}
    </span>
  );
}

function Steps({ access }: { access: ClientAccessView }) {
  // Each stage implies the ones before it: a Live client (e.g. put on a paid plan directly,
  // without a live access request) shows every step as done.
  const live = access.stage === 'Live';
  const approved = access.kybStatus === 'Approved' || live;
  const reached = {
    sandbox: true,
    kyb: approved,
    beta: approved,
    live,
  };
  const items: [string, boolean][] = [
    ['Sandbox', reached.sandbox],
    ['Request approved', reached.kyb],
    ['Beta Live', reached.beta],
    ['Live', reached.live],
  ];
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 small mb-3">
      {items.map(([label, done], i) => (
        <span key={label} className="d-inline-flex align-items-center gap-2">
          {i > 0 && <i className="bi bi-chevron-right text-body-tertiary" />}
          <span className={done ? 'text-success fw-semibold' : 'text-body-secondary'}>
            <i className={`bi ${done ? 'bi-check-circle-fill' : 'bi-circle'} me-1`} />
            {label}
          </span>
        </span>
      ))}
    </div>
  );
}

function BusinessDetails({ access, reviewer = false }: { access: ClientAccessView; reviewer?: boolean }) {
  const address = [
    access.addressLine1,
    access.addressLine2,
    [access.city, access.state, access.postalCode].filter(Boolean).join(', '),
    access.country,
  ]
    .filter(Boolean)
    .join(' · ');
  const rows: [string, ReactNode][] = [
    ['Company name', access.legalBusinessName],
    ['Website', access.businessWebsite],
    ['Company email', access.businessEmail],
    ['Company phone', formatPhoneDisplay(access.businessPhone)],
    [
      'Monthly volume',
      access.projectedMonthlyTransactions != null
        ? `${access.projectedMonthlyTransactions.toLocaleString()} verifications (projected)`
        : null,
    ],
    ['Intended use', access.intendedUse ? <span className="text-break">{access.intendedUse}</span> : null],
    ['Lawful use', access.acceptableUseAcceptedAt ? `Acknowledged ${formatDate(access.acceptableUseAcceptedAt)}` : null],
  ];
  // Collected by the earlier form; shown to the reviewer for requests made with it.
  if (reviewer && (access.ein || access.businessType || address)) {
    rows.push(['EIN / type', `${access.ein ?? '—'} · ${access.businessType ?? '—'}`]);
    rows.push(['Address', address]);
  }
  return (
    <dl className="row small mb-0">
      {rows.map(([k, v]) => (
        <FragmentRow key={k} label={k} value={v} />
      ))}
    </dl>
  );
}

function FragmentRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 text-body-secondary fw-normal">{label}</dt>
      <dd className="col-sm-8">{value || '—'}</dd>
    </>
  );
}

function BetaUsage({ access }: { access: ClientAccessView }) {
  if (access.stage === 'BetaLive') {
    const reached = access.betaUsedToday >= access.betaDailyLimit;
    return (
      <div className="alert alert-primary py-2 small mb-3">
        <strong>Beta Live</strong> until {formatDate(access.betaEndsAt)} &middot;{' '}
        <span className={reached ? 'text-danger fw-semibold' : 'fw-semibold'}>
          {access.betaUsedToday} / {access.betaDailyLimit}
        </span>{' '}
        live verifications today (resets 00:00 UTC). Real IDs, unmasked results, no charge.
      </div>
    );
  }
  if (access.stage === 'BetaEnded') {
    return (
      <div className="alert alert-warning py-2 small mb-3">
        Your Beta Live period ended on {formatDate(access.betaEndsAt)}. Live keys are paused until you choose a
        paid plan; test keys still work.
      </div>
    );
  }
  return null;
}

// ── Client view (My Company) ──────────────────────────────────────────────────

export function LiveAccessCard({
  access,
  canManage,
  onChanged,
}: {
  access: ClientAccessView;
  /** ClientAdmin of this client. */
  canManage: boolean;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="card mt-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">
          <i className="bi bi-shield-check me-2 text-primary" />
          Live access
        </h6>
        <StageBadge stage={access.stage} label={access.stageLabel} outline />
      </div>
      <div className="card-body">
        <Steps access={access} />
        <BetaUsage access={access} />

        {access.kybStatus === 'NotSubmitted' && (
          <>
            <p className="small text-body-secondary">
              The Sandbox returns masked test results. To process real IDs, request live access - we respond within
              24 hours. Once approved you get <strong>Beta Live</strong> free — {access.betaDailyLimit} live
              verifications a day for a limited period — and can then move to a paid plan.
            </p>
            {canManage ? (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                <i className="bi bi-building-check me-1" /> Request live access
              </button>
            ) : (
              <p className="small text-body-secondary mb-0">A company administrator can request live access.</p>
            )}
          </>
        )}

        {access.kybStatus === 'Pending' && (
          <div className="alert alert-info small mb-0">
            <i className="bi bi-hourglass-split me-1" />
            <strong>{access.reviewMessage ?? 'Your request is under review, and you will receive a response within 24 hours.'}</strong>
            <div className="mt-1">
              Submitted {formatDateTime(access.kybSubmittedAt)}. We will email you as soon as it is decided. Meanwhile
              your test keys keep working.
            </div>
          </div>
        )}

        {access.kybStatus === 'Rejected' && (
          <>
            <div className="alert alert-danger small">
              <strong>Your live access request was not approved.</strong>
              {access.kybRejectionReason && <div className="mt-1">Reason: {access.kybRejectionReason}</div>}
            </div>
            {canManage && (
              <button className="btn btn-outline-primary" onClick={() => setShowForm(true)}>
                Correct and resubmit
              </button>
            )}
          </>
        )}

        {access.kybStatus === 'Approved' && (
          <>
            <BusinessDetails access={access} />
            <div className="form-text mt-2">
              <i className="bi bi-lock me-1" />
              Verified company details are locked. Contact support to change them.
            </div>
          </>
        )}
      </div>

      <KybFormModal
        show={showForm}
        access={access}
        onClose={() => setShowForm(false)}
        onSubmitted={() => {
          setShowForm(false);
          onChanged();
        }}
      />
    </div>
  );
}

function KybFormModal({
  show,
  access,
  onClose,
  onSubmitted,
}: {
  show: boolean;
  access: ClientAccessView;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { showSuccess } = useToast();
  // Pre-filled by the API: the last request, else what was given at signup.
  const [form, setForm] = useState<KybSubmitRequest>(() => ({
    companyName: access.draft.companyName,
    companyWebsite: access.draft.companyWebsite,
    companyEmail: access.draft.companyEmail,
    companyPhone: phoneForEdit(access.draft.companyPhone),
    intendedUse: access.draft.intendedUse,
    projectedMonthlyTransactions: access.draft.projectedMonthlyTransactions,
    acceptedAcceptableUse: false,
  }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function set<K extends keyof KybSubmitRequest>(key: K, value: KybSubmitRequest[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = validateWebsite(form.companyWebsite) ?? validatePhone(form.companyPhone, 'Company phone');
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      const r = await submitKyb({
        ...form,
        companyName: form.companyName.trim(),
        companyWebsite: form.companyWebsite.trim(),
        companyEmail: form.companyEmail.trim(),
        companyPhone: form.companyPhone.trim(),
        intendedUse: form.intendedUse.trim(),
      });
      showSuccess(r.message ?? 'Your request is under review.');
      onSubmitted();
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const text = (
    key: 'companyName' | 'companyWebsite' | 'companyEmail' | 'companyPhone',
    label: string,
    opts: { placeholder?: string; type?: string; help?: string } = {},
  ) => (
    <div className="col-md-6">
      <label className="form-label small fw-semibold" htmlFor={`kyb-${key}`}>
        {label}
        <span className="text-danger"> *</span>
      </label>
      <input
        id={`kyb-${key}`}
        className="form-control form-control-sm"
        type={opts.type ?? 'text'}
        placeholder={opts.placeholder}
        required
        value={form[key]}
        {...(key === 'companyPhone' ? digitsOnly : {})}
        onChange={(e) => set(key, key === 'companyPhone' ? formatUsPhoneInput(e.target.value) : e.target.value)}
      />
      {opts.help && <div className="form-text">{opts.help}</div>}
    </div>
  );

  return (
    <Modal show={show} title="Request live access" onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <p className="small text-body-secondary">
          We review every request before an account can process real IDs, and respond within 24 hours. One account
          per business: the website&apos;s domain cannot be used by another account.
        </p>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        <div className="row g-3">
          {text('companyName', 'Company name')}
          {text('companyWebsite', 'Company website', {
            type: 'url',
            placeholder: 'https://yourcompany.com',
            help: 'Full address, starting with http:// or https://.',
          })}
          {text('companyEmail', 'Company email', {
            type: 'email',
            placeholder: 'you@yourcompany.com',
          })}
          {text('companyPhone', 'Company phone number', { type: 'tel' })}
          <div className="col-12">
            <label className="form-label small fw-semibold" htmlFor="kyb-use">
              Description of intended use<span className="text-danger"> *</span>
            </label>
            <textarea
              id="kyb-use"
              className="form-control form-control-sm"
              rows={4}
              required
              minLength={20}
              maxLength={2000}
              placeholder="e.g. Verify the identity of new customers opening accounts on our rental platform before the first booking."
              value={form.intendedUse}
              onChange={(e) => set('intendedUse', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold" htmlFor="kyb-volume">
              Projected monthly transactions<span className="text-danger"> *</span>
            </label>
            <input
              id="kyb-volume"
              type="number"
              min={1}
              max={100000000}
              step={1}
              required
              className="form-control form-control-sm"
              placeholder="e.g. 500"
              value={form.projectedMonthlyTransactions ?? ''}
              onChange={(e) =>
                set('projectedMonthlyTransactions', e.target.value === '' ? null : Number(e.target.value))
              }
            />
            <div className="form-text">Your estimate of ID verifications per month.</div>
          </div>
          <div className="col-12">
            <div className="form-check">
              <input
                id="kyb-ack"
                type="checkbox"
                className="form-check-input"
                required
                checked={form.acceptedAcceptableUse}
                onChange={(e) => set('acceptedAcceptableUse', e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="kyb-ack">
                I confirm, on behalf of the company, that we will not use InertiaID for any unlawful purpose, and will
                use it only in compliance with applicable laws, including identity-verification, privacy and
                anti-discrimination laws.
              </label>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !form.acceptedAcceptableUse}>
            {busy ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Contact details (client-editable) ─────────────────────────────────────────

/**
 * The "Edit contact" dialog, opened from the Account card on My Company (company admins only; the
 * server enforces the same). Shown in a modal rather than a card of its own: a separate Contact card
 * repeated the Account card's email and phone and confused people.
 */
export function ContactEditModal({
  show,
  onClose,
  contactName,
  contactEmail,
  contactPhone,
  onSaved,
}: {
  show: boolean;
  onClose: () => void;
  contactName: string | null;
  contactEmail: string;
  contactPhone: string;
  onSaved: () => void;
}) {
  return (
    <Modal show={show} title="Edit contact" onClose={onClose}>
      {/* Mounted only while open, so every opening starts from the current values. */}
      {show && (
        <ContactForm
          contactName={contactName}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          onCancel={onClose}
          onSaved={() => {
            onSaved();
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

function ContactForm({
  contactName,
  contactEmail,
  contactPhone,
  onCancel,
  onSaved,
}: {
  contactName: string | null;
  contactEmail: string;
  contactPhone: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState(contactName ?? '');
  const [email, setEmail] = useState(contactEmail);
  const [phone, setPhone] = useState(phoneForEdit(contactPhone));
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await updateCompanyContact({ contactName: name.trim() || null, contactEmail: email, contactPhone: phone });
      showSuccess(r.message ?? 'Saved.');
      onSaved();
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save}>
      <div className="mb-2">
        <label className="form-label small mb-1" htmlFor="contactName">Name</label>
        <input id="contactName" className="form-control form-control-sm" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1" htmlFor="contactEmail">Email</label>
        <input id="contactEmail" type="email" className="form-control form-control-sm" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="form-text">Notification emails go to this address and to your company admins.</div>
      </div>
      <div className="mb-3">
        <label className="form-label small mb-1" htmlFor="contactPhone">Phone</label>
        <input
          id="contactPhone"
          type="tel"
          inputMode="tel"
          {...digitsOnly}
          className={`form-control form-control-sm ${phone && validatePhone(phone) ? 'is-invalid' : ''}`}
          required
          value={phone}
          onChange={(e) => setPhone(formatUsPhoneInput(e.target.value))}
        />
        {phone && validatePhone(phone) && <div className="invalid-feedback">{validatePhone(phone)}</div>}
      </div>
      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Save</button>
      </div>
    </form>
  );
}

// ── Decision threshold ────────────────────────────────────────────────────────

export function ThresholdCard({
  access,
  canManage,
  onSave,
  className = 'mt-4',
}: {
  access: ClientAccessView;
  canManage: boolean;
  onSave: (threshold: number | null) => Promise<string | null | undefined>;
  /** Spacing above the card: mt-4 when stacked under another card (Company); none in a grid row. */
  className?: string;
}) {
  const { showSuccess, showError } = useToast();
  const [value, setValue] = useState(access.declineThreshold);
  const [busy, setBusy] = useState(false);

  async function save(threshold: number | null) {
    setBusy(true);
    try {
      showSuccess((await onSave(threshold)) ?? 'Saved.');
      if (threshold === null) setValue(access.platformDeclineThreshold);
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`card ${className}`}>
      <div className="card-header">
        <h6 className="mb-0">
          <i className="bi bi-sliders me-2 text-primary" />
          Decline threshold
        </h6>
      </div>
      <div className="card-body">
        <ThresholdSlider
          id="clientThreshold"
          label="Decline verifications below"
          value={value}
          onChange={setValue}
          disabled={!canManage}
          help={`Applies to each critical field (names, ID number, dates, address), not the average. ${
            access.declineThresholdOverride === null
              ? `Using the platform default (${Math.round(access.platformDeclineThreshold * 100)}%).`
              : `Platform default is ${Math.round(access.platformDeclineThreshold * 100)}%.`
          }`}
        />
        {canManage && (
          <div className="d-flex justify-content-end gap-2 mt-3">
            {access.declineThresholdOverride !== null && (
              <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => void save(null)}>
                Use platform default
              </button>
            )}
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void save(value)}>
              Save threshold
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Super admin review (client page) ──────────────────────────────────────────

export function KybReviewCard({
  clientId,
  access,
  onChanged,
  className = 'mt-4',
}: {
  clientId: number;
  access: ClientAccessView;
  onChanged: () => void;
  /** Spacing above the card: mt-4 when stacked under another card; none in a grid row. */
  className?: string;
}) {
  const { showSuccess, showError } = useToast();
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [dailyLimit, setDailyLimit] = useState<string>(access.betaDailyLimitOverride?.toString() ?? '');
  const [endsAt, setEndsAt] = useState<string>(access.betaEndsAt ? access.betaEndsAt.slice(0, 10) : '');

  async function run(action: () => Promise<{ message?: string | null }>) {
    setBusy(true);
    try {
      showSuccess((await action()).message ?? 'Done.');
      onChanged();
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`card ${className}`}>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">
          <i className="bi bi-building-check me-2 text-primary" />
          Live access request
        </h6>
        <StageBadge stage={access.stage} label={access.stageLabel} outline />
      </div>
      <div className="card-body">
        <Steps access={access} />
        <BetaUsage access={access} />

        {access.kybStatus === 'NotSubmitted' ? (
          <p className="small text-body-secondary mb-0">The client has not requested live access yet.</p>
        ) : (
          <>
            <div className="small mb-2">
              Status: <strong>{access.kybStatus}</strong>
              {access.kybSubmittedAt && <> &middot; submitted {formatDate(access.kybSubmittedAt)}</>}
              {access.kybReviewedAt && (
                <>
                  {' '}
                  &middot; reviewed {formatDate(access.kybReviewedAt)}
                  {access.kybReviewedBy && <> by {access.kybReviewedBy}</>}
                </>
              )}
            </div>
            {access.kybRejectionReason && access.kybStatus === 'Rejected' && (
              <div className="alert alert-danger py-2 small">Rejected: {access.kybRejectionReason}</div>
            )}
            {access.kybStatus === 'Pending' && access.autoApprovesAt && (
              <div className="alert alert-warning py-2 small">
                <i className="bi bi-alarm me-1" />
                Approves automatically at <strong>{formatDateTime(access.autoApprovesAt)}</strong> (
                {Math.max(0, Math.ceil((new Date(access.autoApprovesAt).getTime() - Date.now()) / 3_600_000))} h left)
                unless you approve or reject it first.
              </div>
            )}
            <BusinessDetails access={access} reviewer />
          </>
        )}

        {(access.signupIpAddress || (access.sameIpAccounts?.length ?? 0) > 0) && (
          <div className={`alert ${(access.sameIpAccounts?.length ?? 0) > 0 ? 'alert-warning' : 'alert-light'} py-2 small mt-3 mb-0`}>
            Signed up from <code>{access.signupIpAddress ?? 'unknown'}</code>.
            {(access.sameIpAccounts?.length ?? 0) > 0 ? (
              <>
                {' '}
                Other accounts from the same IP:{' '}
                {access.sameIpAccounts!.map((a, i) => (
                  <span key={a.id}>
                    {i > 0 && ', '}
                    <Link to={`/clients/${a.id}`}>{a.name}</Link>
                  </span>
                ))}
                . Shared office or network, or the same business twice? Check before approving.
              </>
            ) : (
              ' No other accounts from this IP.'
            )}
          </div>
        )}

        {access.kybStatus === 'Pending' && (
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-success" disabled={busy} onClick={() => void run(() => approveKyb(clientId))}>
              <i className="bi bi-check-lg me-1" /> Approve — start Beta Live &amp; send welcome
            </button>
            <button className="btn btn-outline-danger" disabled={busy} onClick={() => setShowReject(true)}>
              <i className="bi bi-x-lg me-1" /> Reject
            </button>
          </div>
        )}

        {access.kybStatus === 'Approved' && access.stage !== 'Live' && (
          <form
            className="row g-2 align-items-end mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              void run(() =>
                updateClientBeta(clientId, dailyLimit.trim() === '' ? null : Number(dailyLimit), endsAt || null),
              );
            }}
          >
            <div className="col-sm-4">
              <label className="form-label small fw-semibold" htmlFor="betaLimit">Beta daily limit</label>
              <input
                id="betaLimit"
                type="number"
                min={1}
                className="form-control form-control-sm"
                placeholder={`default (${access.betaDailyLimit})`}
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
              />
            </div>
            <div className="col-sm-4">
              <label className="form-label small fw-semibold" htmlFor="betaEnds">Beta ends</label>
              <input id="betaEnds" type="date" className="form-control form-control-sm" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
            <div className="col-sm-4">
              <button type="submit" className="btn btn-sm btn-primary w-100" disabled={busy}>Save Beta settings</button>
            </div>
          </form>
        )}
      </div>

      <Modal show={showReject} title="Reject live access request" onClose={() => setShowReject(false)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => rejectKyb(clientId, reason)).then(() => {
              setShowReject(false);
              setReason('');
            });
          }}
        >
          <label className="form-label small" htmlFor="rejectReason">Reason (emailed to the client)</label>
          <textarea id="rejectReason" className="form-control mb-3" rows={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setShowReject(false)}>Cancel</button>
            <button type="submit" className="btn btn-danger" disabled={busy || !reason.trim()}>Reject</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
